import { ResponsiveValue } from '@chakra-ui/react';
import { SpaceProps } from '@chakra-ui/styled-system';

import { HeadingDefaultSpacing } from '../types';

/**
 * Transforms a Heading size (simple or responsive) into corresponding spacing props.
 *
 * ## Why This Exists
 *
 * Chakra UI's theme `sizes` object applies styles via CSS media queries, which have
 * high specificity. When spacing (e.g., `my: '4'`) is included in the sizes config,
 * user overrides like `my={0}` lose to the media query styles — even when passed
 * as props.
 *
 * This helper decouples spacing from the theme and applies it as regular props.
 * Since props are spread BEFORE user overrides (`{...spacingProps} {...rest}`),
 * any user-provided spacing will win.
 *
 * ## Transformation Examples
 *
 * **Simple size:**
 * ```ts
 * getSpacingProps('title-sm', defaultSpacing)
 * // → { my: '4' }
 * ```
 *
 * **Responsive size:**
 * ```ts
 * getSpacingProps({ base: 'title-sm', md: 'title-lg' }, defaultSpacing)
 * // → { my: { base: '4', md: '6' } }
 * ```
 *
 * Generic over the size union so each design version passes only its own steps — the logic is
 * the same for both.
 *
 * @param size - The heading size, either a string or responsive object
 * @param defaultSpacing - Mapping of heading sizes to their default spacing props
 * @returns SpaceProps to apply to the heading, or empty object if no spacing defined
 */
export const getSpacingProps = <Size extends string>(
  size: ResponsiveValue<Size>,
  defaultSpacing: HeadingDefaultSpacing<Size>
): SpaceProps => {
  // Handle simple string size (e.g., "title-md")
  if (typeof size === 'string') {
    return defaultSpacing[size] ?? {};
  }

  // Handle responsive size object (e.g., { base: 'title-sm', md: 'title-lg' })
  // Note: ResponsiveValue can also be an array, but we only support objects
  if (typeof size === 'object' && size !== null && !Array.isArray(size)) {
    return buildResponsiveSpacing(
      size as Record<string, Size | undefined>,
      defaultSpacing
    );
  }

  return {};
};

/**
 * Builds responsive spacing props from a responsive size object.
 *
 * Takes a size object like `{ base: 'title-sm', md: 'title-lg' }` and transforms
 * it into responsive spacing like `{ my: { base: '4', md: '6' } }`.
 *
 * Note: We assume spacing keys are consistent across sizes. We pick the first
 * size in the responsive object that has spacing and use its keys to build
 * responsive values.
 */
const buildResponsiveSpacing = <Size extends string>(
  responsiveSize: Record<string, Size | undefined>,
  defaultSpacing: HeadingDefaultSpacing<Size>
): SpaceProps => {
  // Step 1: Find the first spacing config and use its keys
  const spacingKeys = getSpacingKeysFromFirstMatch(
    responsiveSize,
    defaultSpacing
  );

  if (spacingKeys.length === 0) {
    return {};
  }

  // Step 2: For each spacing key, build a responsive value object
  const responsiveSpacing: SpaceProps = {};

  for (const spacingKey of spacingKeys) {
    const responsiveValue = buildResponsiveValueForKey(
      spacingKey,
      responsiveSize,
      defaultSpacing
    );

    // Only add if we have at least one value
    if (Object.keys(responsiveValue).length > 0) {
      Object.assign(responsiveSpacing, { [spacingKey]: responsiveValue });
    }
  }

  return responsiveSpacing;
};

/**
 * Returns spacing keys from the first size that defines spacing.
 * For simplicity, assumes spacing keys are consistent across sizes.
 */
const getSpacingKeysFromFirstMatch = <Size extends string>(
  responsiveSize: Record<string, Size | undefined>,
  defaultSpacing: HeadingDefaultSpacing<Size>
): Array<keyof SpaceProps> => {
  for (const sizeValue of Object.values(responsiveSize)) {
    if (sizeValue) {
      const spacing = defaultSpacing[sizeValue];
      if (spacing && Object.keys(spacing).length > 0) {
        return Object.keys(spacing) as Array<keyof SpaceProps>;
      }
    }
  }

  return [];
};

/**
 * Builds a responsive value object for a single spacing key.
 *
 * Example: For spacingKey='my' and responsiveSize={ base: 'title-sm', md: 'title-lg' }
 * Returns: { base: '4', md: '6' }
 *
 * Assumes all sizes have the spacing key (guaranteed by getSpacingKeysFromFirstMatch).
 */
const buildResponsiveValueForKey = <Size extends string>(
  spacingKey: keyof SpaceProps,
  responsiveSize: Record<string, Size | undefined>,
  defaultSpacing: HeadingDefaultSpacing<Size>
): Record<string, SpaceProps[keyof SpaceProps]> => {
  const responsiveValue: Record<string, SpaceProps[keyof SpaceProps]> = {};

  for (const [breakpoint, sizeValue] of Object.entries(responsiveSize)) {
    if (sizeValue) {
      responsiveValue[breakpoint] = defaultSpacing[sizeValue][spacingKey];
    }
  }

  return responsiveValue;
};
